import React from "react";
import {
  Badge,
  Box,
  Button,
  Heading,
  HStack,
  Icon,
  Image,
  SimpleGrid,
  Stack,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { FiExternalLink } from "react-icons/fi";
import {
  formatFundCurrency,
  formatFundNumber,
  getFundRedemptionPriceLabel,
  getFundLogoSrc,
  getFundSubscriptionPriceLabel,
  isFundInvestmentEnabled,
  isSepahBrokerFund,
} from "../funds/fundCardUtils";
import { getFundCategoryByRegisterCode } from "../../config/fundCategories";

const MotionBox = motion(Box);

export type ProfileFundCardEntry = {
  fundRegisterCode?: string;
  fundName?: string;
  fundSummaryName?: string;
  fundTypeTitle?: string;
  annualReturn?: number;
  reInvest?: boolean;
  reInvestPercent?: number;
  nav?: {
    subscriptionNav?: number;
    cancelNav?: number;
  };
  stats: {
    totalAsset: number;
    totalAssetPercent?: number;
    totalProfit: number;
    totalUnits: number;
    summary: Record<string, any>;
  };
};

const parseNumericValue = (value: unknown) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === "string") {
    const normalizedValue = value.replaceAll(",", "").trim();
    const parsedValue = Number(normalizedValue);
    return Number.isFinite(parsedValue) ? parsedValue : 0;
  }

  return 0;
};

const formatPercentDisplay = (value: unknown, digits = 2) => {
  const numericValue = parseNumericValue(value);
  return Number.isInteger(numericValue)
    ? String(numericValue)
    : numericValue.toFixed(digits).replace(/\.?0+$/, "");
};

const getImportantStats = (fundProfile: ProfileFundCardEntry) => {
  const efficiency = parseNumericValue(fundProfile.stats?.summary?.efficiency);
  const receivableDividendSum = parseNumericValue(
    fundProfile.stats?.summary?.receivableDividendSum,
  );
  const totalProfit = fundProfile.stats?.totalProfit || 0;

  const stats = [
    {
      label: "دارایی",
      value: `${formatFundCurrency(fundProfile.stats?.totalAsset || 0)} ریال`,
      tone: "brand.600",
    },
    {
      label: "سود کل",
      value: `${formatFundCurrency(totalProfit)} ریال`,
      tone: totalProfit >= 0 ? "green.500" : "red.500",
    },
  ];

  if (!totalProfit && efficiency) {
    stats[1] = {
      label: "بازدهی",
      value: `${formatPercentDisplay(efficiency)}%`,
      tone: efficiency >= 0 ? "green.500" : "red.500",
    };
  }

  if (!totalProfit && !efficiency && receivableDividendSum) {
    stats[1] = {
      label: "سود قابل دریافت",
      value: `${formatFundCurrency(receivableDividendSum)} ریال`,
      tone: "orange.500",
    };
  }

  return stats.slice(0, 2);
};

type ProfileFundCardProps = {
  fundProfile: ProfileFundCardEntry;
  index?: number;
  mode: "compact" | "full";
  onSubscription?: (fundRegisterCode?: string) => void;
  onRedemption?: (fundRegisterCode?: string) => void;
  onDetails?: (fundRegisterCode?: string) => void;
};

export const ProfileFundCard = ({
  fundProfile,
  index = 0,
  mode,
  onSubscription,
  onRedemption,
  onDetails,
}: ProfileFundCardProps) => {
  const cardBg = useColorModeValue("white", "dark.50");
  const borderColor = useColorModeValue("gray.100", "gray.700");
  const mutedColor = useColorModeValue("gray.500", "gray.400");
  const labelColor = useColorModeValue("gray.600", "gray.300");
  const subtleBg = useColorModeValue("gray.50", "dark.900");

  const fundRegisterCode = fundProfile.fundRegisterCode;
  const fundName =
    fundProfile.fundName?.trim() || fundProfile.stats?.summary?.groupName;
  const fundSummaryName = String(fundProfile.fundSummaryName || "").trim();
  const logoSrc = fundRegisterCode
    ? getFundLogoSrc(String(fundRegisterCode))
    : null;
  const statItems = getImportantStats(fundProfile);
  const isFull = mode === "full";
  const isSepahFund = isSepahBrokerFund(fundRegisterCode);
  const subscriptionPriceLabel =
    getFundSubscriptionPriceLabel(fundRegisterCode);
  const redemptionPriceLabel = getFundRedemptionPriceLabel(fundRegisterCode);
  const categoryConfig = getFundCategoryByRegisterCode(fundRegisterCode);
  const displayTitle = fundSummaryName || fundName || "—";
  const annualReturn = parseNumericValue(fundProfile.annualReturn);
  const isPositiveAnnualReturn = annualReturn >= 0;
  const subscriptionActionLabel = isSepahFund ? "خرید" : "صدور";
  const redemptionActionLabel = isSepahFund ? "فروش" : "ابطال";
  const isInvestmentDisabled = !isFundInvestmentEnabled(fundRegisterCode);

  return (
    <MotionBox
      key={String(fundRegisterCode || index)}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      h="100%"
      transition={{
        duration: 0.38,
        ease: "easeOut",
        delay: index * 0.06,
      }}
    >
      <Box
        bg={cardBg}
        borderRadius="3xl"
        border="1px solid"
        borderColor={borderColor}
        overflow="hidden"
        boxShadow="md"
        transition="all 0.24s ease"
        h="100%"
        display="flex"
        flexDirection="column"
        _hover={{
          transform: "translateY(-4px)",
          boxShadow: "xl",
          borderColor: "brand.200",
        }}
      >
        <Box p={5} flex="1" display="flex" flexDirection="column">
          <HStack
            align="start"
            justify="space-between"
            spacing={4}
            mb={5}
            w="full"
          >
            <HStack align="center" spacing={4} minW={0}>
              <Box
                w="72px"
                h="72px"
                borderRadius="2xl"
                bg={subtleBg}
                display="flex"
                alignItems="center"
                justifyContent="center"
                overflow="hidden"
                flexShrink={0}
                p={2}
              >
                {logoSrc ? (
                  <Image
                    src={logoSrc}
                    alt={displayTitle}
                    maxW="100%"
                    maxH="100%"
                    objectFit="contain"
                  />
                ) : (
                  <Text fontWeight="900" color={labelColor}>
                    {fundRegisterCode || "—"}
                  </Text>
                )}
              </Box>

              <VStack align="start" spacing={2} minW={0}>
                <Heading size="sm" lineHeight="1.8" noOfLines={2}>
                  {displayTitle}
                </Heading>
                {fundName ? (
                  <Text
                    fontSize="xs"
                    color={mutedColor}
                    lineHeight="1.6"
                    noOfLines={2}
                  >
                    {fundName}
                  </Text>
                ) : null}
                <HStack spacing={2} flexWrap="wrap">
                  {categoryConfig ? (
                    <Badge
                      colorScheme={categoryConfig.colorScheme}
                      variant="subtle"
                      borderRadius="full"
                      px={3}
                      py={1}
                    >
                      {categoryConfig.shortTitle}
                    </Badge>
                  ) : null}
                </HStack>
              </VStack>
            </HStack>
          </HStack>

          <SimpleGrid columns={2} spacing={3} mb={4}>
            {statItems.map((stat) => (
              <Box
                key={stat.label}
                bg={subtleBg}
                borderRadius="2xl"
                px={4}
                py={4}
                textAlign="center"
              >
                <Text fontSize="xs" color={mutedColor} mb={2}>
                  {stat.label}
                </Text>
                <Text
                  fontSize="sm"
                  lineHeight="1.8"
                  fontWeight="900"
                  color={stat.tone}
                  noOfLines={2}
                >
                  {stat.value}
                </Text>
              </Box>
            ))}
          </SimpleGrid>

          <Box bg={subtleBg} borderRadius="2xl" px={4} py={4} mb={4}>
            <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={3}>
              <Box textAlign="center">
                <Text fontSize="xs" color={mutedColor} mb={1}>
                  تعداد واحد
                </Text>
                <Text fontWeight="800">
                  {formatFundNumber(fundProfile.stats?.totalUnits || 0)} واحد
                </Text>
              </Box>
              <Box textAlign="center">
                <Text fontSize="xs" color={mutedColor} mb={1}>
                  سود قابل دریافت
                </Text>
                <Text fontWeight="800">
                  {formatFundCurrency(
                    parseNumericValue(
                      fundProfile.stats?.summary?.receivableDividendSum,
                    ),
                  )}{" "}
                  ریال
                </Text>
              </Box>
              <Box textAlign="center">
                <Text fontSize="xs" color={mutedColor} mb={1}>
                  سهم از کل دارایی
                </Text>
                <Text fontWeight="800">
                  {formatPercentDisplay(fundProfile.stats?.totalAssetPercent)}٪
                </Text>
              </Box>
            </SimpleGrid>
          </Box>

          <Box
            bg={subtleBg}
            borderRadius="2xl"
            px={4}
            py={4}
            mb={4}
            textAlign="center"
          >
            <Text fontSize="xs" color={mutedColor} mb={1}>
              بازدهی سالانه
            </Text>
            <Text
              fontSize="2xl"
              fontWeight="800"
              color={isPositiveAnnualReturn ? "green.500" : "red.500"}
            >
              {formatPercentDisplay(annualReturn)}٪
            </Text>
          </Box>

          {isFull ? (
            <>
              <SimpleGrid columns={2} spacing={3} mb={4}>
                <Badge
                  bg="green.50"
                  color="green.700"
                  px={2.5}
                  py={2}
                  borderRadius="full"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  textAlign="center"
                  whiteSpace="nowrap"
                  fontSize="xs"
                  _dark={{ bg: "green.900", color: "green.100" }}
                >
                  {subscriptionPriceLabel}:{" "}
                  {formatFundNumber(
                    parseNumericValue(fundProfile.nav?.subscriptionNav),
                  )}{" "}
                  ریال
                </Badge>
                <Badge
                  bg="red.50"
                  color="red.700"
                  px={2.5}
                  py={2}
                  borderRadius="full"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  textAlign="center"
                  whiteSpace="nowrap"
                  fontSize="xs"
                  _dark={{ bg: "red.900", color: "red.100" }}
                >
                  {redemptionPriceLabel}:{" "}
                  {formatFundNumber(
                    parseNumericValue(fundProfile.nav?.cancelNav),
                  )}{" "}
                  ریال
                </Badge>
              </SimpleGrid>

              <Stack direction="column" spacing={3} mt="auto">
                <HStack spacing={3}>
                  <Button
                    flex="1"
                    borderRadius="full"
                    isDisabled={isInvestmentDisabled}
                    onClick={() => onSubscription?.(fundRegisterCode)}
                  >
                    {subscriptionActionLabel}
                  </Button>
                  <Button
                    flex="1"
                    borderRadius="full"
                    variant="outline"
                    isDisabled={isInvestmentDisabled}
                    onClick={() => onRedemption?.(fundRegisterCode)}
                  >
                    {redemptionActionLabel}
                  </Button>
                </HStack>
                <Button
                  w="100%"
                  borderRadius="full"
                  variant="outline"
                  rightIcon={<Icon as={FiExternalLink as any} />}
                  isDisabled={!fundRegisterCode}
                  onClick={() => onDetails?.(fundRegisterCode)}
                >
                  اطلاعات بیشتر
                </Button>
                {/* مدیریت سود فقط در صفحه سرمایه‌گذاری صندوق‌های مجاز نمایش داده می‌شود.
                <Button
                  w="100%"
                  borderRadius="full"
                  variant="outline"
                  rightIcon={<Icon as={FiPercent as any} />}
                  isDisabled={isSepahFund || isInvestmentDisabled}
                  onClick={() => onReinvest?.(fundProfile)}
                >
                  مدیریت سود
                </Button> */}
              </Stack>
            </>
          ) : null}
        </Box>
      </Box>
    </MotionBox>
  );
};
